import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckOutlined } from '@mui/icons-material';
import { Box, Button, Chip, Divider, IconButton, useMediaQuery, useTheme } from '@mui/material';
import BDrawer from '@ui/components/common/BDrawer';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import CustomDialog from '@ui/components/common/Dialog';
import TextEditor from '@ui/components/TextEditor';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FamilyFestFaqTypeEnum } from '@application/enums/family-fest-faq-type-enum';
import { useTranslation } from 'react-i18next';
import { Iconly } from 'react-iconly';
import { useFamilyFestFaqCategoryOptions, useFaqTypeOptions } from '../FaqTypeFilter';
const AddFaqDialog = ({ open, onClose, categories = [], isFamilyEvent = false, defaultFaqType = FamilyFestFaqTypeEnum.Registration, onCreate, editFaq, onSave }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const faqTypeOptions = useFaqTypeOptions();
    const isEditMode = !!editFaq;
    const emptyForm = (faqType) => ({
        category: null,
        faqType,
        faqItems: [{ question: '', answer: '' }]
    });
    const { control, handleSubmit, watch, reset, setValue, formState: { isSubmitting } } = useForm({ defaultValues: emptyForm(defaultFaqType) });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'faqItems'
    });
    const categoryValue = watch('category');
    const selectedCategory = categoryValue ?? null;
    const selectedFaqType = watch('faqType');
    // FamilyFest: the chosen type's categories come from the backend.
    const { options: familyFestCategoryOptions, isLoading: isLoadingCategories } = useFamilyFestFaqCategoryOptions(selectedFaqType, isFamilyEvent && open);
    const visibleCategories = isFamilyEvent
        ? familyFestCategoryOptions
        : categories.map((c) => ({ label: c.label, value: c.value, icon: c.iconUrl }));
    // The type the form was opened with; switching away from it clears the category.
    const faqTypeAtOpenRef = React.useRef(defaultFaqType);
    React.useEffect(() => {
        faqTypeAtOpenRef.current = defaultFaqType;
        if (!open) {
            reset(emptyForm(defaultFaqType));
            return;
        }
        if (isEditMode && editFaq) {
            reset({
                category: editFaq.faqCategoryId ?? null,
                faqType: defaultFaqType,
                faqItems: [{ question: editFaq.question, answer: editFaq.answer }]
            });
        }
        else {
            reset(emptyForm(defaultFaqType));
        }
    }, [open, editFaq, isEditMode, reset, defaultFaqType]);
    // A category of the other type can't stay selected after switching the type.
    React.useEffect(() => {
        if (isFamilyEvent && selectedFaqType !== faqTypeAtOpenRef.current) {
            faqTypeAtOpenRef.current = selectedFaqType;
            setValue('category', null);
        }
    }, [selectedFaqType, isFamilyEvent, setValue]);
    const handleAddMore = () => {
        append({ question: '', answer: '' });
    };
    const handleCancel = () => {
        reset(emptyForm(defaultFaqType));
        onClose();
    };
    const submitForm = handleSubmit(async (data) => {
        if (isEditMode && editFaq) {
            const isSuccess = await onSave?.({
                id: editFaq.id,
                categoryId: data.category,
                question: data.faqItems[0].question,
                answer: data.faqItems[0].answer
            });
            if (isSuccess) {
                onClose();
            }
        }
        else {
            const chosenCategory = visibleCategories.find((c) => c.value === data.category);
            onCreate?.({
                category: chosenCategory ? { label: chosenCategory.label, value: chosenCategory.value } : null,
                faqItems: data.faqItems
            });
        }
    });
    const dialogTitle = isEditMode
        ? t('events.eventDetails.faqTab.dialog.editFaq')
        : t('events.eventDetails.faqTab.dialog.createNewFaqs');
    const body = (_jsxs(Box, { p: 2, display: 'flex', flexDirection: 'column', gap: 2, children: [isFamilyEvent && (_jsx(SingleSelect, { bg: 'grey8', control: control, name: 'faqType', label: t('events.eventDetails.faqTab.faqType.label'), options: faqTypeOptions, disableClearable: true })), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'category', label: t('events.eventDetails.faqTab.dialog.category'), options: visibleCategories, loading: isLoadingCategories, placeholder: t('events.eventDetails.faqTab.dialog.selectCategory') }), _jsx(Divider, {}), fields.map((field, index) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', children: [!isEditMode && fields.length > 1 && index > 0 ? (_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 0.5, children: [_jsx(Chip, { variant: 'filled', color: 'default', size: 'small', label: index + 1, style: { paddingLeft: '3px', paddingRight: '3px' } }), _jsx(IconButton, { size: 'small', color: 'error', style: { border: 'none' }, onClick: () => remove(index), children: _jsx(Iconly, { name: 'Delete', set: 'curved' }) })] })) : null, _jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 1, children: [_jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: `faqItems.${index}.question`, label: t('events.eventDetails.faqTab.dialog.question'), placeholder: t('events.eventDetails.faqTab.dialog.enterQuestion') }), _jsx(Controller, { control: control, name: `faqItems.${index}.answer`, render: ({ field }) => (_jsx(TextEditor, { value: field.value, onChange: field.onChange, placeholder: t('events.eventDetails.faqTab.dialog.enterAnswer'), hideToolbar: true, showLinkTool: true, placeholderColor: 'rgba(0,0,0,0.6)' })) }), index !== fields.length - 1 ? _jsx(Divider, {}) : null] })] }, field.id))), !isEditMode && (_jsx(Button, { fullWidth: true, variant: 'soft', color: 'primary', onClick: handleAddMore, children: t('events.eventDetails.faqTab.dialog.addMore') }))] }));
    const actions = (_jsxs(_Fragment, { children: [_jsx(Button, { fullWidth: true, variant: 'outlined', color: 'inherit', size: 'large', onClick: handleCancel, disabled: isSubmitting, children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', startIcon: _jsx(CheckOutlined, {}), onClick: submitForm, disabled: !selectedCategory || isSubmitting, children: isEditMode ? t('actions.saveChanges') : t('actions.createNow') })] }));
    if (isSmallScreen) {
        return (_jsx(BDrawer, { anchor: 'bottom', open: open, onClose: handleCancel, title: dialogTitle, showBottomBar: true, dialogActions: actions, children: body }));
    }
    return (_jsx(CustomDialog, { open: open, onClose: handleCancel, titleWithIcon: dialogTitle, titleIcon: '/assets/images/question-mark.png', actions: actions, maxWidth: 'sm', backgroundVariant: 'white', closeIcon: true, children: _jsx(Box, { p: 1.5, sx: {
                maxHeight: '65vh',
                overflowY: 'auto'
            }, children: body }) }));
};
export default AddFaqDialog;
